import { useCallback } from "react";
import { v4 as generateUniqueId } from "uuid";
import { useOffer } from "hooks/useOffer";
import { storeSessionId } from "services/onlineApplicationService";
import { getFirst30CharsOfSessionId } from "utils/sessionIdUtils";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { saveSessionId, markSessionCreatedFired } from "store/application/applicationSlice";
import { GA_EVENT_CONFIGS, GA_DEDUP_KEYS } from "constants/gaEvents";
import { pushGTMEvent } from "utils/gtmEvent";
import { pushGTMEventOnce } from "utils/gaEventDedup";
import { useProductContext } from "context/ProductContext";

export const useSessionId = () => {
    const { offerData } = useOffer();
    const { isGIWL } = useProductContext();
    const dispatch = useAppDispatch();

    const existingSessionId = useAppSelector((state) => state.application.sessionId);
    const existingSaveContId = useAppSelector((state) => state.application.saveContId);
    const hasSessionCreatedEventFired = useAppSelector((state) => state.application.saveContSessionCreatedFired);

    const processSessionId = useCallback(
        async (invitationCodeOverride?: string): Promise<void> => {
            const finderNum = invitationCodeOverride ?? offerData?.invitationCode;

            // All products use saveContId; DTO/MLTA require finderNum to be present before storing
            if (!isGIWL && !finderNum) {
                return;
            }

            // GIWL session is bound at flow entry by useGIWLQuoteHydration (reads session_id
            // from URL). If saveContId is already set, there is nothing left to do here.
            if (isGIWL && existingSaveContId) {
                return;
            }

            // If sessionId and saveContId already exist in Redux (from sessionStorage), reuse them.
            // Only DTO/MLTA reaches this block — GIWL returns early above.
            if (existingSessionId && existingSaveContId) {
                try {
                    await storeSessionId({ userRef: finderNum!, saveContId: existingSaveContId });
                    pushGTMEventOnce(`${GA_DEDUP_KEYS.RESUMED}:${existingSaveContId}`, GA_EVENT_CONFIGS.SAVE_CONTINUE_RESUMED);
                } catch (error) {
                    console.error("[useSessionId] Error sending existing sessionID to storeSessionId:", error);
                }
                return;
            }

            // Generate a new UUID only if it doesn't exist
            const newSessionId = generateUniqueId();
            const sessionIdFirst30Chars = getFirst30CharsOfSessionId(newSessionId);

            if (!sessionIdFirst30Chars) {
                return;
            }

            dispatch(
                saveSessionId({
                    sessionId: newSessionId,
                    saveContId: sessionIdFirst30Chars,
                })
            );

            try {
                await storeSessionId({ userRef: finderNum!, saveContId: sessionIdFirst30Chars });

                if (!hasSessionCreatedEventFired) {
                    pushGTMEvent(GA_EVENT_CONFIGS.SAVE_CONTINUE_SESSION_CREATED);
                    dispatch(markSessionCreatedFired());
                }
            } catch (error) {
                console.error("[useSessionId] Error sending sessionID to storeSessionId:", error);
            }
        },
        [isGIWL, offerData?.invitationCode, dispatch, existingSessionId, existingSaveContId, hasSessionCreatedEventFired]
    );

    return { processSessionId };
};
